<script setup lang="ts">
import { richTextOrNull } from '@/composables/helpers/html';

const ctaEllipseTopRight = '/img/2026/cta/cta-desktop-top-right-ellipse.png';
const ctaEllipseBottomRight = '/img/2026/cta/cta-desktop-bottom-right-ellipse.png';
const ctaEllipseBottomLeft = '/img/2026/cta/cta-desktop-bottom-left-ellipse.png';
const ctaEllipseBottomLeftOrange = '/img/2026/cta/cta-desktop-bottom-left-ellipse-orange.png';
const ctaMobileTopRight = '/img/2026/cta/cta-mobile-top-right-ellipse.png';
const ctaMobileBottomRight = '/img/2026/cta/cta-mobile-bottom-right-ellipse.png';
const defaultCtaImg = '/img/2026/cta.jpg';

interface CtaButton {
    label?: string;
    title?: string;
    url?: string;
}

interface CtaImage {
    src?: string;
    altText?: string;
}

const props = withDefaults(defineProps<{
    headline?: string;
    copy?: string;
    image?: CtaImage;
    button?: CtaButton;
    variant?: 'purple' | 'orange';
    imageShape?: 'shard' | 'rectangle';
}>(), {
    headline: 'Secure your spot at UNBOUND26',
    variant: 'purple',
    imageShape: 'shard',
});

const imgSrc = computed(() => props.image?.src || defaultCtaImg);
// Builder's image input supplies `alt` (see inputs.js); `altText` is the VIDEO
// input's field name. Tolerate both so any content already carrying altText works.
const imgAlt = computed(() => props.image?.alt || props.image?.altText || 'INBOUND attendees');

// Relative/local paths (served from /public) shouldn't be routed through the
// Cloudflare image provider — that provider's baseURL points at thisismess.io
// and fails for dev-only assets. Use a plain <img> for those.
const isLocalImg = computed(() => {
    const src = imgSrc.value || '';
    return src.startsWith('/') || src.startsWith('./') || (!/^[a-z]+:\/\//i.test(src) && !src.startsWith('data:'));
});
const isOrange = computed(() => props.variant === 'orange');

// The image area is shard-shaped by default: clipped to #cta-shard and filled
// edge to edge with object-cover, which is right for a crowd photo where the
// crop costs nothing.
//
// It is wrong for artwork that has to be seen whole. The mobile-app promo is a
// device mockup — cropping it to a shard cuts the phone in half — so
// 'rectangle' keeps the SAME BOUNDS and drops the three things that assume a
// photo: the clip, the cover crop, and the pink gradient that would tint a
// screenshot. Nothing about the layout moves.
const isRectangle = computed(() => props.imageShape === 'rectangle');
const bottomLeftEllipse = computed(() => isOrange.value ? ctaEllipseBottomLeftOrange : ctaEllipseBottomLeft);

const btnLabel = computed(() => props.button?.label || 'Register now');
const btnUrl = computed(() => props.button?.url || '/register');
const btnTitle = computed(() => props.button?.title || btnLabel.value);
</script>

<template>
    <section class="block-cta relative isolate
        flex flex-col text-white
        lg:flex-row lg:min-h-[603px]"
        :class="`variant-${variant}`">

        <!-- Photo -->
        <div class="relative z-[1] w-full lg:w-1/2 overflow-hidden lg:pt-[119px] order-2 lg:order-none">
            <div class="relative w-full aspect-[709/484] overflow-hidden
                    lg:absolute lg:bottom-0 lg:left-0 lg:max-h-full lg:max-w-[709px]"
                :class="isRectangle ? 'cta-image-rect' : 'cta-image-mask'">
                <img v-if="isLocalImg"
                    :src="imgSrc"
                    :alt="imgAlt"
                    loading="lazy"
                    decoding="async"
                    class="absolute inset-0 w-full h-full"
                    :class="isRectangle ? 'object-contain' : 'object-cover'" />
                <MessImage v-else
                    :image="props.image || { src: defaultCtaImg, alt: 'INBOUND attendees' }" lazy
                    class="absolute! inset-0 size-full"
                    :class="isRectangle ? '' : '[&_img]:object-cover'" />
                <!-- Pink gradient overlay. Photo-only: over a device mockup it
                     washes the screenshot pink. -->
                <div v-if="!isRectangle" class="absolute inset-0 pointer-events-none"
                    style="background: linear-gradient(to bottom, rgba(253, 46, 203, 0) 0%, rgba(253, 46, 203, 0.5) 100%);" />
            </div>
        </div>

        <!-- Content -->
        <div class="relative z-[2] flex flex-col items-start gap-[24px]
            px-[var(--box-padding-lr-m)] py-[60px]
            lg:w-1/2 lg:px-[40px] lg:py-[150px] lg:gap-[31px] lg:justify-center">

            <div class="flex flex-col gap-[18px] w-full">
                <h2 class="font-hubspot-serif normal-case mb-0 font-normal
                    text-[calc(36/var(--base-fs)*1rem)] leading-[1.1]
                    lg:text-[calc(60/var(--base-fs)*1rem)]">
                    {{ headline }}
                </h2>
                <div v-if="richTextOrNull(copy)" class="copy-text font-hubspot-sans font-light
                    text-[calc(16/var(--base-fs)*1rem)] leading-[26px] tracking-[0.32px]"
                    v-html="copy" />
            </div>

            <a :href="btnUrl" :title="btnTitle"
                v-posthog-capture="{ name: 'cta_clicked', properties: { cta_type: 'cta_block', button_label: btnLabel, button_url: btnUrl } }"
                class="inline-flex items-center gap-2.5
                    bg-white text-(--magenta) no-underline
                    rounded-[50px] px-5 py-2.5
                    font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px]
                    hover:bg-(--orange) hover:text-white
                    transition-colors duration-200
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white
                    focus-visible:ring-offset-2 focus-visible:ring-offset-(--purple)">
                {{ btnLabel }}
            </a>
        </div>

        <!-- Background: decorative ellipses -->
        <div aria-hidden="true" class="absolute inset-0 z-[0] pointer-events-none overflow-hidden">
            <!-- Desktop -->
            <img v-if="!isOrange" :src="ctaEllipseTopRight" loading="lazy" decoding="async" alt=""
                class="hidden lg:display-block absolute top-0 right-0" />
            <img v-if="!isOrange" :src="ctaEllipseBottomRight" loading="lazy" decoding="async" alt=""
                class="hidden lg:display-block absolute bottom-0 right-0" />
            <img :src="bottomLeftEllipse" loading="lazy" decoding="async" alt=""
                class="hidden lg:display-block absolute bottom-0 left-0" />
            <!-- Mobile -->
            <img v-if="!isOrange" :src="ctaMobileTopRight" loading="lazy" decoding="async" alt=""
                class="display-block lg:hidden absolute top-0 right-0" />
            <img v-if="!isOrange" :src="ctaMobileBottomRight" loading="lazy" decoding="async" alt=""
                class="display-block lg:hidden absolute bottom-0 right-0" />
        </div>
    </section>
</template>

<style>
.block-cta.variant-purple {
    background: linear-gradient(
        196.87deg,
        var(--purple) 8.54%,
        var(--purple) 59.45%,
        rgb(219, 54, 49) 99.09%
    );
}
@media (min-width: 1024px) {
    .block-cta.variant-purple {
        background: linear-gradient(
            248.18deg,
            var(--purple) 0%,
            var(--purple) 49%,
            rgb(227, 58, 38) 100%
        );
    }
}

/* Orange variant — orange at top, purple at bottom */
.block-cta.variant-orange {
    background: linear-gradient(
        193.03deg,
        var(--orange) 10.65%,
        var(--purple) 91.92%
    );
}
.block-cta .copy-text * {
    font-family: var(--font-hubspot-sans);
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    font-weight: 300;
    color: white;
}
.block-cta .copy-text p:not(:last-child) {
    margin-bottom: 1em;
}
.cta-image-mask {
    clip-path: url(#cta-shard);
    transform: scaleX(-1);
}
.cta-image-mask > * {
    transform: scaleX(-1);
}
@media (min-width: 1024px) {
    .cta-image-mask {
        transform: none;
    }
    .cta-image-mask > * {
        transform: none;
    }
}
/* Rectangle variant: same box, no clip and no mobile mirror — the flip exists
   to face the shard the other way and would mirror a screenshot.
   MessImage sets `object-fit: cover` at `.mess-image picture img`, which is
   more specific than Tailwind's `[&_img]:` utility (a descendant selector with
   two element parts beats one), so contain has to be won here rather than with
   a utility class that would silently lose. */
.cta-image-rect .mess-image picture img {
    object-fit: contain;
}
</style>
